import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Input from '../../components/Input';
import Button from '../../components/Button';
import { authService } from '../../services';
import { isValidEmail } from '../../utils/auth';
import routes from '../../utils/routes';
import './ForgotPassword.css';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);
  const [serverError, setServerError] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);
  const [email, setEmail] = useState('');

  // Form validation schema
  const validationSchema = Yup.object({
    email: Yup.string()
      .email('Invalid email address')
      .required('Email is required')
      .test('is-valid-email', 'Invalid email format', isValidEmail),
  });

  // Formik form handling
  const formik = useFormik({
    initialValues: {
      email: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        setIsLoading(true);
        setServerError('');
        
        console.log('Requesting verification code for password reset:', values.email);
        
        // Request verification code - this just sends a code to the email, doesn't reset password
        await authService.forgotPassword(values.email);
        
        // Show success message
        setIsSuccess(true);
        setEmail(values.email);
        
        // Navigate to OTP verification page after a delay
        setTimeout(() => {
          navigate(routes.public.VERIFY_OTP, { 
            state: { 
              email: values.email,
              isPasswordReset: true
            } 
          });
        }, 3000);
      } catch (error) {
        console.error('Forgot password error:', error);
        setServerError(
          error.response?.data?.message || 
          'An error occurred. Please try again.'
        );
      } finally {
        setIsLoading(false);
      }
    },
  });

  return (
    <div className="forgot-password-container">
      <div className="forgot-password-card">
        <div className="forgot-password-header">
          <div className="forgot-password-title">Forgot Password</div>
          <div className="forgot-password-subtitle">
            Enter your email to reset your password
          </div>
        </div>

        {isSuccess ? (
          <div className="forgot-password-success">
            <p>We've sent a verification code to {email}</p>
            <p>Redirecting to verification page...</p>
          </div>
        ) : (
          <>
            {serverError && (
              <div className="error-message" style={{ marginBottom: 'var(--spacing-md)' }}>
                {serverError}
              </div>
            )}

            <form className="forgot-password-form" onSubmit={formik.handleSubmit}>
              <Input
                name="email"
                type="email"
                label="Email Address"
                value={formik.values.email}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.email && formik.errors.email}
                placeholder="Enter your email"
                required
              />

              <Button
                type="submit"
                variant="primary"
                className="forgot-password-submit-button"
                disabled={isLoading}
              >
                {isLoading ? 'Sending...' : 'Reset Password'}
              </Button>
            </form>
          </>
        )}

        <div className="forgot-password-back">
          <Link to={routes.public.LOGIN}>Back to Login</Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword; 